<template>
  <v-app>
    <base-nav-drawer v-if="navDrawerOpen" />
    <router-view name="login"></router-view>

    <div v-if="displayNavBar">
      <user-navbar
        v-if="userProfile.profile"
        @impersonating="impersonating = true"
      ></user-navbar>
      <project-owner-navbar2 v-else></project-owner-navbar2>
    </div>

    <v-content
      v-if="
        $route.name !== 'login' &&
          $route.name !== 'latestMembers' &&
          $route.name !== 'latestMembersGettingStarted' &&
          $route.name !== 'latestShowcases' &&
          $route.name !== 'latestShowcasesGettingStarted' &&
          $route.name !== 'latestItinerariesGettingStarted' &&
          $route.name !== 'latestItineraries' &&
          $route.name !== 'profileShowcasesGettingStarted' &&
          $route.name !== 'profileShowcases'
      "
      class="white pb-0"
    >
      <ks-bar
        v-if="impersonating"
        :userProfile="userProfile"
        :ksName="ksName"
      ></ks-bar>
      <v-container
        fluid
        class="white fill-height pb-0"
        grid-list-lg
        :class="[
          $vuetify.breakpoint.xl ? 'xl-margins' : '',
          $route.name !== 'login' ? 'navbar-padding' : 'py-0 px-0'
        ]"
      >
        <v-row
          :class="[$route.meta.hasBackBtn ? 'justify-start' : 'justify-center']"
          class="fill-height"
        >
          <v-col
            v-if="$route.meta.hasBackBtn && !$route.meta.fullScreenWidth"
            cols="1"
          >
            <v-row justify="center">
              <v-btn
                @click="routeHandler()"
                fab
                large
                color="brand"
                class="white--text"
                height="48px"
                width="48px"
              >
                <v-icon>{{ icons.mdiArrowLeft }}</v-icon>
              </v-btn>
            </v-row>
          </v-col>
          <v-col
            :cols="!$route.meta.fullScreenWidth ? 10 : 12"
            class="px-0 mx-0 pb-0 fill-height"
          >
            <router-view></router-view>
          </v-col>
        </v-row>
      </v-container>
      <base-footer></base-footer>
    </v-content>

    <v-content
      v-else-if="
        $route.name === 'latestMembers' ||
          $route.name === 'latestMembersGettingStarted' ||
          $route.name === 'latestShowcases' ||
          $route.name === 'latestShowcasesGettingStarted' ||
          $route.name === 'latestItinerariesGettingStarted' ||
          $route.name === 'latestItineraries' ||
          $route.name === 'profileShowcasesGettingStarted' ||
          $route.name === 'profileShowcases'
      "
    >
      <router-view></router-view>
    </v-content>
  </v-app>
</template>

<script>
const UserNavbar = () =>
  import('./components/base_components/UserNavbar/UserNavbar.vue');
const ProjectOwnerNavbar2 = () =>
  import(
    './components/base_components/ProjectOwnerNavbar/ProjectOwnerNavbar2.vue'
  );
const BaseFooter = () =>
  import('./components/base_components/BaseFooter/BaseFooter.vue');
const BaseNavDrawer = () =>
  import('@/components/base_components/BaseNavDrawer/BaseNavDrawer.vue');
const KsBar = () => import('@/components/KsBar.vue');

import { mapState, mapActions } from 'vuex';
import store from './store/store';
import Cookies from 'js-cookie';
import { mdiArrowLeft } from '@mdi/js';
import {
  VUE_APP_FILE_URL,
  VUE_APP_BRAND_TITLE
} from '@/utilities/constants.js';
import { setInitialScripts } from './utilities/modules/iubenda.mjs';
import { eventBus } from './utilities/eventbus';
import { bootstrap } from 'vue-gtag';

export default {
  metaInfo() {
    return {
      title: VUE_APP_BRAND_TITLE
    };
  },

  components: {
    ProjectOwnerNavbar2,
    BaseNavDrawer,
    BaseFooter,
    KsBar,
    UserNavbar
  },

  data() {
    return {
      tour: true,
      ksToken: null,
      ksName: null,
      impersonating: false,
      requiredPasswordReset: false
    };
  },

  async beforeCreate() {
    const token = Cookies.get('bearerToken');
    //remove persisted state if no token available
    if (!token) {
      localStorage.removeItem('vuex');
    }

    if (!store.state.brand.brand.hasOwnProperty('name')) {
      try {
        const res = await store.dispatch('getBrand');

        //This is used to listen for a network error and redirect to a service unavailable page
        if (!res?.data && res.toJSON().message === 'Network Error') {
          this.$router.push('/network-error');
        }

        const favicon = document.querySelector('#favicon');
        favicon.setAttribute('href', `${VUE_APP_FILE_URL}/${res.data.favicon}`);

        const mainTitle = document.querySelector('#main-title');
        mainTitle.textContent = res.data.name;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      }
    } else {
      const favicon = document.querySelector('#favicon');
      favicon.setAttribute(
        'href',
        `${VUE_APP_FILE_URL}/${store.state.brand.brand.favicon}`
      );

      const mainTitle = document.querySelector('#main-title');
      mainTitle.textContent = store.state.brand.brand.name;
    }
  },

  created() {
    this.setAppLanguage();
    this.setIubenda();
  },

  mounted() {
    eventBus.$on('iubendaConsent', this.handleIubendaConsent);

    // Sync logout across tabs via a dedicated signal key
    window.addEventListener('storage', this.handleStorageChange);
  },

  updated() {
    this.checkIfImpersonating();
  },

  beforeDestroy() {
    eventBus.$off('iubendaConsent', this.handleIubendaConsent);
    window.removeEventListener('storage', this.handleStorageChange);
  },

  methods: {
    ...mapActions([
      'login',
      'getTranslatedAppLabels',
      'getBrand',
      'getUserProfile',
      'checkIfProfileImpersonating'
    ]),

    handleStorageChange(event) {
      // Only react to the dedicated logout signal, not general vuex writes
      if (event.key === 'logout_event' && event.newValue) {
        const hasProfile = this.$store.state.authentication?.userProfile?.profile?.profileId;
        if (hasProfile) {
          this.$store.dispatch('resetAllState');
          this.$router.push('/login');
        }
      }
    },

    routeHandler() {
      this.$router.go(-1);
    },

    checkIfImpersonating() {
      const ksToken = Cookies.get('ksBearerToken');
      const ksName = Cookies.get('ksName');

      if (ksToken !== undefined) {
        this.impersonating = true;
        this.checkIfProfileImpersonating(true);
      } else {
        this.impersonating = false;
        this.checkIfProfileImpersonating(false);
      }
      if (ksName !== undefined) this.ksName = ksName;
    },

    setAppLanguage() {
      //Fist use the 'current' language from local storage then turn to profile lang and use en as fallback
      const localStorageLang = localStorage.getItem('langOption');
      const userProfileLang = this.userProfile?.profile?.defaultLanguage;

      //use browser language after profile lang as fall back
      let browserLang = navigator.language.split('-')[0];

      if (browserLang !== 'it' && browserLang !== 'es' && browserLang !== 'zh') browserLang = 'en';

      const initialLangToUse =
        localStorageLang || userProfileLang || browserLang || 'en';

      store.state.defaultLanguage = initialLangToUse;

      const payload = {
        code: initialLangToUse
      };
      this.getTranslatedAppLabels(payload);
    },

    setIubenda() {
      const path = window.location.pathname;
      if (!path.includes('/latest') && !path.includes('/profile-showcases')) {
        setInitialScripts();
      }
    },

    handleIubendaConsent() {
      bootstrap().then(gtag => {
        // all done!
      });
    }
  },

  computed: {
    ...mapState({
      navDrawerOpen: state => state.navDrawerOpen,
      appLabels: state => state.languages.appLabels,
      userProfile: state => state.authentication.userProfile,
      brand: state => state.brand.brand
    }),

    icons: () => ({ mdiArrowLeft }),

    lastRoute() {
      const lastRoute = localStorage.getItem('lastRoute');

      return lastRoute;
    },

    displayNavBar() {
      const namedViewsToExclude = [
        'networkError',
        'login',
        'latestMembers',
        'latestMembersGettingStarted',
        'latestShowcases',
        'latestShowcasesGettingStarted',
        'latestItinerariesGettingStarted',
        'latestItineraries',
        'profileShowcasesGettingStarted',
        'profileShowcases'
      ];
      const routeName = this.$route.name;

      if (namedViewsToExclude.includes(routeName)) return false;
      if (
        routeName === 'viewSelectedProject' &&
        this.$vuetify.breakpoint.mdAndUp
      )
        return false;
      if (
        routeName === 'viewSelectedProjectGroup' &&
        this.$vuetify.breakpoint.mdAndUp
      )
        return false;

      return true;
    }
  }
};
</script>

<style>
@font-face {
  font-family: 'Nunito';
  font-style: normal;
  font-weight: 400;
  src: url('./assets/fonts/nunito-v22-latin-regular.woff2') format('woff2'),
    url('./assets/fonts/nunito-v22-latin-regular.woff') format('woff'),
    url('./assets/fonts/nunito-v22-latin-regular.ttf') format('truetype');
  font-display: swap;
}

@import url('https://fonts.googleapis.com/css2?family=Noto+Sans+SC:wght@400;700&display=swap');

* {
  font-family: 'Nunito', 'Noto Sans SC', sans-serif;
  font-display: swap;
}

h1,
h2,
h3,
h4,
h5,
h6,
p {
  color: #34495e;
  word-break: normal !important;
}

.brand--body {
  color: #34495e;
}

.brand--border {
  border: 1px solid var(--v-brand-base);
}

.border-dashed {
  border: 1px dashed rgba(0, 0, 0, 0.16);
}

.cursor-pointer {
  cursor: pointer;
}

.cursor-disabled {
  cursor: not-allowed !important;
}

.cursor-move {
  cursor: move;
}

.xl-margins {
  margin-left: 115px;
  margin-right: 115px;
}

.navbar-padding {
  padding-top: 40px;
}

.gradient-loader {
  animation-duration: 1.8s;
  animation-fill-mode: forwards;
  animation-iteration-count: infinite;
  animation-name: placeHolderShimmer;
  animation-timing-function: linear;
  background: #f6f7f8;
  background: linear-gradient(
    to right,
    #fffefe 8%,
    #fcfdfd 38%,
    #f9fafc 54%,
    #f6f8fa 80%
  );
  background-size: 1000px 640px;

  position: relative;
}

@keyframes placeHolderShimmer {
  0% {
    background-position: -468px 0;
  }
  100% {
    background-position: 468px 0;
  }
}

/*
  Vuetify Overrides & Additions
*/
.v-toolbar {
  /*
    The toolbar needs a z-index of 1 because the list items that have menu dropdowns
    need to sit "on top" of the main content. This way we avoid those items showing
    "under" content in the main section.
  */
  z-index: 1;
}

div.v-dialog__container {
  display: block !important;
}

/* Shephard.js Overrides */
.shepherd-text {
  padding-left: 2rem !important;
  padding-right: 2rem !important;
  padding-bottom: 3rem !important;
}

.shepherd-has-title .shepherd-content .shepherd-header {
  background: transparent !important;
  padding-top: 0 !important;
  padding-left: 2rem !important;
  padding-right: 2rem !important;
}

.shepherd-has-title .shepherd-content .shepherd-header .shepherd-title {
  font-size: 1.1rem !important;
  text-transform: uppercase !important;
}

.shepherd-footer {
  padding-left: 2rem !important;
  padding-right: 2rem !important;
}

.shepherd-button {
  align-items: center !important;
  border-radius: 4px !important;
  display: inline-flex !important;
  flex: 0 0 auto !important;
  font-weight: 500;
  font-size: 0.75rem !important;
  letter-spacing: 0.0892857143em !important;
  justify-content: center !important;
  max-width: 100% !important;
  outline: 0 !important;
  position: relative !important;
  text-decoration: none !important;
  text-indent: 0.0892857143em !important;
  text-transform: uppercase !important;
  transition-duration: 0.28s !important;
  transition-property: box-shadow, transform, opacity !important;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1) !important;
  user-select: none !important;
  vertical-align: middle !important;
  white-space: nowrap !important;
}

.shepherd-button:not(:only-child):first-child {
  background-color: transparent !important;
  color: var(--v-primary-base) !important;
}

.shepherd-button:not(:only-child):first-child:hover {
  background-color: rgba(0, 0, 0, 0.05) !important;
  transition: cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.shepherd-button:last-child {
  background-color: var(--v-brand-base) !important;
  color: white !important;
}

.shepherd-content {
  margin-top: 20px !important;
}

.horizontal-scroll-bar::-webkit-scrollbar {
  height: 8px;
  border-radius: 5px;
}

/* Track */
.horizontal-scroll-bar::-webkit-scrollbar-track {
  background: #f6f8fa;
  border-radius: 5px;
}

/* Handle */
.horizontal-scroll-bar::-webkit-scrollbar-thumb {
  background: #d0cfcf;
  border-radius: 5px;
}

/* Handle on hover */
.horizontal-scroll-bar::-webkit-scrollbar-thumb:hover {
  background: #888;
  border-radius: 5px;
}

.horizontal-scroll-bar {
  flex-wrap: nowrap !important;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.vertical-scroll-bar::-webkit-scrollbar {
  width: 8px;
  border-radius: 5px;
}

/* Track */
.vertical-scroll-bar::-webkit-scrollbar-track {
  background: #f6f8fa;
  border-radius: 5px;
}

/* Handle */
.vertical-scroll-bar::-webkit-scrollbar-thumb {
  background: #d0cfcf;
  border-radius: 5px;
}

/* Handle on hover */
.vertical-scroll-bar::-webkit-scrollbar-thumb:hover {
  background: #888;
  border-radius: 5px;
}

.vertical-scroll-bar {
  overflow-y: auto;
}

/* Gloabl Scrollbar */
/* width */
.scroll-bar::-webkit-scrollbar {
  width: 5px;
  height: 8px;
  border-radius: 5px;
}
/* Track */
.scroll-bar::-webkit-scrollbar-track {
  background: #f6f8fa;
  border-radius: 5px;
}
/* Handle */
.scroll-bar::-webkit-scrollbar-thumb {
  background: #d0cfcf;
  border-radius: 5px;
}
/* Handle on hover */
.scroll-bar::-webkit-scrollbar-thumb:hover {
  background: #888;
  border-radius: 5px;
}

.v-card__text,
.v-card__title {
  word-break: normal !important;
}

form .v-text-field .v-input__control .v-input__slot {
  min-height: auto !important;
  display: flex !important;
  align-items: center !important;
}
/*  */

.grey-scale {
  filter: grayscale(100);
}
</style>
